import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { getMaquinaByTienda, getMaquinaTienda,
  getMaquinaByComuna, getStockProductosPorMaquinaGeneroYMarca,
  getMachineList, getBrandsByMaquina, getGendersByMaquina,
  getMaquinaByID, getDiscountCode, getProductosDestacadosByMaquina, getMaquinasByDistance } from "../api";
import { normalizarListaMaquinas } from "../lib/mapaUbicaciones";


export const ListaMaquinasByTienda = () => {
  const queryClient = useQueryClient();
  const query = useMutation({

  mutationKey: ['mutateListaMaquinasByTienda'],
  mutationFn: getMaquinaByTienda,
    onSuccess: (status) => {
      queryClient.setQueryData(
        ['listaMaquinasTienda'],
        status
      );
      return status;
    },
  });

  return query;

}

export const ListaMaquinasTienda = () => {
  const query = useQuery(
    ['listaMaquinasTienda'],
    () => getMaquinaTienda(),
    {
      staleTime: 1000 * 60 * 10,// 10 Minutos
    }
  );

  return query;
}

// `getMaquinaByComuna` puede resolver HTTP 200 con
// {status:"error", message:...} (parámetros inválidos) — normalizarListaMaquinas
// (src/lib/mapaUbicaciones.js) garantiza que sólo se escriba un array al
// cache; un payload "invalido" no se escribe (queda lo que ya había, casi
// siempre []) y se reporta como advertencia manejada, nunca como crash.
export const ListaMaquinasByComuna = () => {
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['mutateListaMaquinasByComuna'],
    mutationFn: getMaquinaByComuna,
    onSuccess: (status) => {
      const { maquinas, estado } = normalizarListaMaquinas(status);

      if (estado === "invalido") {
        Sentry.captureMessage("Respuesta no-array de listaMaquinas", {
          level: "warning",
          tags: { feature: "ubicaciones_modal" },
          extra: { status: status?.status, message: status?.message },
        });
        return status;
      }

      queryClient.setQueryData(
        ['ListaMaquinasByComuna'],
        maquinas
      );
      return status;
    },
  });

  return query;
}

export const ListaMaquinasByDistance = (latitud, longitud, empresa_id) => {
  const data = { 
    latitud: Number(latitud), 
    longitud: Number(longitud), 
    empresa_id: Number(empresa_id) 
  };
    
    // 2. LA FUNCIÓN DE FETCH USA EL OBJETO CREADO
    const fetchMaquinas = () => getMaquinasByDistance(data);

    // 3. USO DE useQuery
    const query = useQuery({
        // La queryKey tiene los valores primitivos.
        queryKey: ['listaMaquinasByDistance', latitud, longitud, empresa_id], 
        queryFn: fetchMaquinas,
        
        // Desactivamos la ejecución automática. Ahora el useEffect la controlará.
        enabled: false, 
        
        staleTime: 1000 * 60 * 10, // 10 Minutos
        // Establece 'retry' en false si quieres depurar errores de inmediato.
        retry: false,
    });

    // 4. Devolvemos el objeto query COMPLETO, incluyendo la función refetch
    return query;
};

export const useStockProductsByMachine = (idMaquina, idGenero, idMarca) => {
  const query = useQuery(
    ['stockProductsByMachine'],
    async () => getStockProductosPorMaquinaGeneroYMarca(idMaquina, idGenero, idMarca),
    {
      staleTime: 1000 * 60 * 10,// 10 Minutos
      retry : false
    }
  );
  return query;
}

export const ListaMaquinas = () => {
  const query = useQuery(
    ['listaMaquinas'],
    () => getMachineList(),
    {
      staleTime: 1000 * 60 * 10,// 10 Minutos
    }
  );
  return query;
}

export const ListaMaquinasPorEmpresa = (latitud, longitud, empresa_id) => {
  const data = { 
    latitud: Number(latitud), 
    longitud: Number(longitud), 
    empresa_id: Number(empresa_id) 
  };
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['mutateListaMaquinasPorEmpresa'],
    mutationFn: () => getMaquinasByDistance(data),
    onSuccess: (status) => {
      queryClient.setQueryData(
        ['ListaMaquinasPorEmpresa'],
        status
      );
    },
  });

  return query;
};

export const useMaquinaById = () => {
  const query = useMutation({
    mutationKey: ['useMaquinaById'],
    mutationFn: getMaquinaByID,
    onSuccess: (status) => {
      return status;
    },
  });
  return query;
};

export const ListaMarcasPorMaquina = ( idMaquina = 0 ) => {
  const query = useQuery(
    ['listaMarcasPorMaquina', idMaquina],
    () => getBrandsByMaquina(idMaquina),
    {
      staleTime: 1000 * 60 * 10,// 10 Minutos
    }
  );
  return query;
}

export const ListaGenerosPorMaquina = ( idMaquina = 0 ) => {
  const query = useQuery(
    ['listaGenerosPorMaquina', idMaquina],
    () => getGendersByMaquina(idMaquina),
    {
      staleTime: 1000 * 60 * 10,// 10 Minutos
    }
  );
  return query;
}

export const CodigoDeDescuento = () => {
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['codigoDeDescuento'],
    mutationFn: getDiscountCode,
    onSuccess: (status) => {
      queryClient.setQueryData(
        ['codigoDeDescuento'],
        status,
        {
          updatedAt: new Date().getTime() + 100000
        }
      );
      return status;
    },
  });

  return query;
}

export const DestacadosByMaquina = (idMaquina) => {
  const query = useQuery({
    queryKey: ['destacadosByMaquina', idMaquina], 
    queryFn: () => getProductosDestacadosByMaquina(idMaquina),
    enabled: !!idMaquina, 
    staleTime: 1000 * 60 * 10, // 10 Minutos
  });
  return query;
};